// Settings, Templates (spec C14): the emails to the insured, one row each with its subject, "Edited"
// or "Department template", and the date of the last change. A row opens the editor (TemplateEditor).
import { useCallback, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { Api, EmailTemplateRow, TemplatesView } from '../auth/api';
import { PageHeader } from '../shell/PageHeader';
import { Toast } from '../shell/Toast';
import { SettingsNav } from '../settings/SettingsNav';
import { stamp } from '../settings/code-rule-lines';
import { TemplateEditor } from '../settings/TemplateEditor';
import { templateCopy as c } from '../settings/template-lines';

export type TemplatesLoaderData = TemplatesView;

const columns = 'minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 0.9fr)';

export function TemplatesPage({ api }: { api: Api }) {
  const view = useLoaderData() as TemplatesLoaderData;
  const revalidator = useRevalidator();
  const [editing, setEditing] = useState<EmailTemplateRow | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const saved = useCallback(
    (text: string) => {
      setToast({ id: Date.now(), text });
      revalidator.revalidate();
    },
    [revalidator],
  );

  return (
    <>
      <PageHeader title="Settings" subtitle="Changes apply to everyone in the department" />
      <div className="setwrap">
        <SettingsNav current="/settings/templates" />
        <div className="setcol">
          <div className="col g4">
            <h2 className="dtitle">{c.title}</h2>
            <span className="t12 muted">{c.sub}</span>
          </div>
          <div className="hdr" style={{ gridTemplateColumns: columns }}>
            {['Template', 'Subject', 'Status'].map((h) => (
              <div key={h} className="th t12 muted w5">
                <span className="ell">{h}</span>
              </div>
            ))}
          </div>
          <div className="gcard">
            {view.templates.map((t) => (
              <div
                key={t.key}
                className="trow"
                style={{ display: 'grid', gridTemplateColumns: columns, alignItems: 'center' }}
              >
                <div className="td vis inscell">
                  <button
                    type="button"
                    className="ins"
                    aria-haspopup="dialog"
                    aria-label={`Open ${t.name}`}
                    onClick={() => setEditing(t)}
                  >
                    {t.name}
                  </button>
                </div>
                <div className="td">
                  <span className="ell t13 text2">{t.subject}</span>
                </div>
                <div className="td">
                  {t.isDefault ? (
                    <span className="tag t-grey">{c.standard}</span>
                  ) : (
                    <span className="tag t-blue">
                      {c.edited}
                      {t.updatedAt ? `, ${stamp(t.updatedAt)}` : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {editing && (
        <TemplateEditor
          key={editing.key}
          api={api}
          view={view}
          template={view.templates.find((t) => t.key === editing.key) ?? editing}
          onClose={close}
          onSaved={saved}
        />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </>
  );
}
